/**
 * useClickLock — Per-key in-flight + post-call cooldown guard for buttons
 * that can fire fire-and-forget mutations.
 *
 * Returns:
 *   - `isLocked(key)` — true while the key's mutation is in flight OR
 *     during the trailing cooldown window.
 *   - `run(key, fn)` — invokes `fn`; ignores the call if the key is
 *     locked. Always holds the lock for at least `cooldownMs` after `fn`
 *     resolves so a fast double-tap never slips through.
 *
 * Use case: ExecutionPlanStack's "Let's go!" / "Cancel" buttons used to
 * fire `onCancel(batchId)` six times when the user clicked rapidly,
 * because the parent re-renders before the YDoc state catches up and
 * the card had no per-batch in-flight guard. This hook keeps the lock
 * keyed by batchId across re-renders.
 *
 * The pure state-machine behavior is exposed via `createClickLock` so
 * it can be exercised in vitest without a React renderer.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

export interface UseClickLockResult {
  isLocked: (key: string) => boolean
  run: (key: string, fn: () => void | Promise<void>) => Promise<void>
}

interface ClickLockMachine {
  isLocked: (key: string) => boolean
  run: (key: string, fn: () => void | Promise<void>) => Promise<void>
  dispose: () => void
  /** Test-only: read the current locked-keys set as an array. */
  _snapshot: () => string[]
}

interface CreateClickLockOptions {
  cooldownMs: number
  onChange?: () => void
  /** Override `setTimeout` for tests. Default: global setTimeout. */
  scheduleUnlock?: (cb: () => void, ms: number) => (() => void) | void
}

/**
 * Pure state machine — no React. Exposed for unit tests; React consumers
 * should use the `useClickLock` hook below.
 */
export function createClickLock(opts: CreateClickLockOptions): ClickLockMachine {
  const locked = new Set<string>()
  const cancelUnlocks = new Set<() => void>()
  let generation = 0
  const schedule =
    opts.scheduleUnlock ??
    ((cb, ms) => {
      const timeoutId = setTimeout(cb, ms)
      return () => clearTimeout(timeoutId)
    })

  const notify = () => {
    if (opts.onChange) opts.onChange()
  }

  return {
    isLocked: (key) => locked.has(key),
    _snapshot: () => Array.from(locked),
    dispose() {
      generation += 1
      for (const cancelUnlock of cancelUnlocks) {
        cancelUnlock()
      }
      cancelUnlocks.clear()
      locked.clear()
    },
    async run(key, fn) {
      if (locked.has(key)) return
      const runGeneration = generation
      locked.add(key)
      notify()
      try {
        await Promise.resolve(fn())
      } catch (err) {
        // Swallow — `run` is called fire-and-forget from button onClick
        // handlers; the wrapped fn is responsible for its own error
        // surfacing. We must not re-throw, or onClick handlers see an
        // unhandled rejection on every retryable failure.
        // eslint-disable-next-line no-console
        console.warn('[useClickLock] wrapped fn threw:', err)
      } finally {
        if (runGeneration === generation) {
          // Hold the lock for the cooldown window so the user can't
          // double-tap through the round-trip.
          const cancelUnlockRef: { current?: () => void } = {}
          const releaseLock = () => {
            if (runGeneration !== generation) return
            if (cancelUnlockRef.current) {
              cancelUnlocks.delete(cancelUnlockRef.current)
            }
            if (locked.delete(key)) {
              notify()
            }
          }
          const cancelUnlock = schedule(releaseLock, opts.cooldownMs) ?? undefined
          cancelUnlockRef.current = cancelUnlock
          if (cancelUnlock) {
            cancelUnlocks.add(cancelUnlock)
          }
        }
      }
    },
  }
}

export function useClickLock(cooldownMs = 1000): UseClickLockResult {
  // Force re-renders so consumers' `isLocked` reads pick up changes.
  const [, forceRender] = useState(0)

  const machine = useMemo(
    () =>
      createClickLock({
        cooldownMs,
        onChange: () => forceRender((n) => n + 1),
      }),
    [cooldownMs],
  )

  useEffect(() => () => machine.dispose(), [machine])

  const isLocked = useCallback((key: string) => machine.isLocked(key), [machine])
  const run = useCallback(
    (key: string, fn: () => void | Promise<void>) => machine.run(key, fn),
    [machine],
  )

  return { isLocked, run }
}
